import { ReactElement } from 'react'
import { Formik, getIn } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Label } from 'components/v2/Label'
import { getUnprocessableErrors, isConflictError } from 'utils/error'
import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { INewPassengerTypeForm, INewPassengerTypeFormProps, INewPassengerTypeProps } from './types'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { SelectCountries, useCountry } from 'components/SelectCountries'
import { GTLanguage } from 'gql/types.generated'
import { SelectPassengerType } from '../SelectPassengerType'
import { schema } from './schema'
import { TCountryCode } from 'types/index'
import { InputPercentage } from 'components/InputPercentage'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'

const messages = defineMessages({
  passengerTypePlaceholder: {
    id: 'passengerType.new.form.name.placeholder',
    defaultMessage: 'Passenger Type'
  },
  enLabelPlaceholder: {
    id: 'passengerType.new.form.enLabel.placeholder',
    defaultMessage: 'Label'
  },
  esLabelPlaceholder: {
    id: 'passengerType.new.form.esLabel.placeholder',
    defaultMessage: 'Etiqueta'
  },
  enLabelPluralPlaceholder: {
    id: 'passengerType.new.form.enLabelPlural.placeholder',
    defaultMessage: "Label's plural"
  },
  esLabelPluralPlaceholder: {
    id: 'passengerType.new.form.esLabelPlural.placeholder',
    defaultMessage: 'Plural de la etiqueta'
  },
  enAbbreviationPlaceholder: {
    id: 'passengerType.new.form.enAbbreviation.placeholder',
    defaultMessage: 'Abbreviation'
  },
  esAbbreviationPlaceholder: {
    id: 'passengerType.new.form.esAbbreviation.placeholder',
    defaultMessage: 'Abreviación'
  },
  enDescriptionPlaceholder: {
    id: 'passengerType.new.form.enDescription.placeholder',
    defaultMessage: 'Description'
  },
  esDescriptionPlaceholder: {
    id: 'passengerType.new.form.esDescription.placeholder',
    defaultMessage: 'Descripción'
  },
  salesChannelsPlaceholder: {
    id: 'passengerType.new.form.salesChannels.placeholder',
    defaultMessage: 'Select Sales Channels'
  },
  selectCountryCode: {
    id: 'passengerType.new.form.selectCountryCode',
    defaultMessage: 'Select Country Code'
  },
  discountPlaceholder: {
    id: 'passengerType.new.form.discount.placeholder',
    defaultMessage: 'Discount'
  }
})

const NewPassengerTypeForm = (props: INewPassengerTypeFormProps): ReactElement => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    error,
    loading,
    isEdit,
    dirty
  } = props

  const intl = useIntl()
  const country = useCountry(values.countryCode)

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column width="50%">
            <SelectCountries
              value={values.countryCode}
              onChange={async (value): Promise<void> => {
                await setFieldValue('countryCode', value)
                setFieldValue('salesChannels', [])
              }}
              onBlur={handleBlur}
              touched={touched.countryCode}
              errors={errors.countryCode}
              disabled={isEdit}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectPassengerType
              onChange={(value): void => {
                setFieldValue('passengerType', value)
              }}
              onBlur={handleBlur}
              value={values.passengerType ?? []}
              touched={touched.passengerType}
              errors={errors.passengerType}
              disabled={isEdit}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="enLabel"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.enLabel}
              placeholder={intl.formatMessage(messages.enLabelPlaceholder)}
              touched={touched.enLabel}
              errors={errors.enLabel}
              label={<FormattedMessage id="passengerType.new.form.enLabel.label" defaultMessage="English Label" />}
            />
          </Column>
          <Column width="50%">
            <Input
              id="esLabel"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.esLabel}
              placeholder={intl.formatMessage(messages.esLabelPlaceholder)}
              touched={touched.esLabel}
              errors={errors.esLabel}
              label={<FormattedMessage id="passengerType.new.form.esLabel.label" defaultMessage="Spanish Label" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="enLabelPlural"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.enLabelPlural}
              placeholder={intl.formatMessage(messages.enLabelPluralPlaceholder)}
              touched={touched.enLabelPlural}
              errors={errors.enLabelPlural}
              label={
                <FormattedMessage
                  id="passengerType.new.form.enLabelPlural.label"
                  defaultMessage="English Label's Plural"
                />
              }
            />
          </Column>
          <Column width="50%">
            <Input
              id="esLabelPlural"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.esLabelPlural}
              placeholder={intl.formatMessage(messages.esLabelPluralPlaceholder)}
              touched={touched.esLabelPlural}
              errors={errors.esLabelPlural}
              label={
                <FormattedMessage
                  id="passengerType.new.form.esLabelPlural.label"
                  defaultMessage="Spanish Label's Plural"
                />
              }
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="enAbbreviation"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.enAbbreviation}
              placeholder={intl.formatMessage(messages.enAbbreviationPlaceholder)}
              touched={touched.enAbbreviation}
              errors={errors.enAbbreviation}
              label={
                <FormattedMessage
                  id="passengerType.new.form.enAbbreviation.label"
                  defaultMessage="English Abbreviation"
                />
              }
            />
          </Column>
          <Column width="50%">
            <Input
              id="esAbbreviation"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.esAbbreviation}
              placeholder={intl.formatMessage(messages.esAbbreviationPlaceholder)}
              touched={touched.esAbbreviation}
              errors={errors.esAbbreviation}
              label={
                <FormattedMessage
                  id="passengerType.new.form.esAbbreviation.label"
                  defaultMessage="Spanish Abbreviation"
                />
              }
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              id="enDescription"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.enDescription}
              placeholder={intl.formatMessage(messages.enDescriptionPlaceholder)}
              touched={touched.enDescription}
              errors={errors.enDescription}
              label={
                <FormattedMessage
                  id="passengerType.new.form.enDescription.label"
                  defaultMessage="English Description"
                />
              }
            />
          </Column>
          <Column width="50%">
            <Input
              id="esDescription"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.esDescription}
              placeholder={intl.formatMessage(messages.esDescriptionPlaceholder)}
              touched={touched.esDescription}
              errors={errors.esDescription}
              label={
                <FormattedMessage
                  id="passengerType.new.form.esDescription.label"
                  defaultMessage="Spanish Description"
                />
              }
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="addonService.form.salesChannels.label" defaultMessage="Sales Channel" />
            </Label>
            <SelectSalesChannelsV2
              countryId={country?.id}
              id="salesChannels"
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              values={values.salesChannels}
              onChange={(salesChannels: ISalesChannel[]): void => {
                setFieldValue('salesChannels', salesChannels)
              }}
              touched={getIn(touched, 'salesChannels')}
              errors={getIn(errors, 'salesChannels')}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="loyalty.campaign.rule.conditions.totalBookingValue" defaultMessage="Discount" />
            </Label>
            <InputPercentage
              disabled={false}
              value={values.discount}
              placeholder="0%"
              onChange={(value: INewPassengerTypeForm['discount']): void => {
                setFieldValue('discount', value)
              }}
              onBlur={handleBlur}
              touched={touched.discount}
              errors={errors.discount}
            />
          </Column>
        </Row>
      </Card.Content>
      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              {isConflictError(error) ? (
                <FormattedMessage
                  id="passengerType.new.form.error.conflict"
                  defaultMessage="Passenger type exists in the country and sales channel"
                />
              ) : (
                <FormattedMessage
                  id="passengerType.new.form.error"
                  defaultMessage="There was an error while saving your passenger type"
                />
              )}
            </ErrorMessage>
          </div>
        )}
        <Card.FooterActions>
          <Button as={NavLink} to="/passenger-types" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading} disabled={!dirty}>
            {props.isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

export const NewPassengerType = ({
  onSubmit,
  error,
  loading,
  isEdit,
  passengerTypeData
}: INewPassengerTypeProps): ReactElement => {
  const intl = useIntl()
  const getInitialValues = (): INewPassengerTypeForm => {
    if (isEdit && passengerTypeData) {
      return {
        discount: String(passengerTypeData.discount),
        passengerType: passengerTypeData.name,
        countryCode: passengerTypeData.countryCode as TCountryCode,
        salesChannels: passengerTypeData.salesChannelsV2 ?? [],
        enLabel:
          passengerTypeData.label?.find((label) => label.language === GTLanguage.En && !label.plural)?.text ?? '',
        esLabel:
          passengerTypeData.label?.find((label) => label.language === GTLanguage.Es && !label.plural)?.text ?? '',
        enLabelPlural:
          passengerTypeData.label?.find((label) => label.language === GTLanguage.En && label.plural)?.text ?? '',
        esLabelPlural:
          passengerTypeData.label?.find((label) => label.language === GTLanguage.Es && label.plural)?.text ?? '',
        enAbbreviation:
          passengerTypeData.abbreviation?.find((abbreviation) => abbreviation.language === GTLanguage.En)?.text ?? '',
        esAbbreviation:
          passengerTypeData.abbreviation?.find((abbreviation) => abbreviation.language === GTLanguage.Es)?.text ?? '',
        enDescription:
          passengerTypeData.description?.find((description) => description.language === GTLanguage.En)?.text ?? '',
        esDescription:
          passengerTypeData.description?.find((description) => description.language === GTLanguage.Es)?.text ?? ''
      }
    }
    return {
      discount: '',
      passengerType: '',
      countryCode: '',
      salesChannels: [],
      enLabel: '',
      esLabel: '',
      enLabelPlural: '',
      esLabelPlural: '',
      enAbbreviation: '',
      esAbbreviation: '',
      enDescription: '',
      esDescription: ''
    }
  }
  const initialValues = getInitialValues()
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={schema(intl)}>
      {(props): ReactElement => {
        return (
          <NewPassengerTypeForm
            {...props}
            error={error}
            loading={loading}
            isEdit={isEdit}
            passengerTypeData={passengerTypeData}
          />
        )
      }}
    </Formik>
  )
}
